import { AppError, type ErrorCode } from '@st/contract';
import type { TFunction } from './I18nProvider';

export interface ErrorMessage {
  code: ErrorCode | 'network';
  title: string;
  help: string;
  /** Kamo poruka vodi — ARHITEKTURA.md sekcija 10 traži da svaka greška ponudi izlaz. */
  actionKey: 'retry' | 'login' | 'billing' | 'plans' | 'sync' | 'none';
}

/**
 * `AppError.code` → poruka iz `@st/i18n`.
 * Server nikad ne diktira tekst; klijent bira poruku po stabilnom kodu.
 */
const MAP: Record<ErrorCode | 'network', { key: string; action: ErrorMessage['actionKey'] }> = {
  bad_request: { key: 'error.validation', action: 'retry' },
  validation_failed: { key: 'error.validation', action: 'retry' },
  unauthorized: { key: 'error.sessionExpired', action: 'login' },
  session_expired: { key: 'error.sessionExpired', action: 'login' },
  forbidden: { key: 'error.forbidden', action: 'none' },
  not_found: { key: 'error.notFound', action: 'none' },
  conflict: { key: 'error.conflict', action: 'retry' },
  rate_limited: { key: 'error.rateLimited', action: 'retry' },
  /*
    402 u ovom sustavu NIJE neuspjelo plaćanje nego zaključan sadržaj: bacaju ga
    edukacijski članci, treninzi iza probnog pristupa i paketi bez besplatnih
    treninga. „Plaćanje nije prošlo" bi slalo korisnika na karticu koja nikad nije
    ni terećena, pa poruka kaže što je stvarno posrijedi, a izlaz je pregled paketa.
    Stvarni problem s naplatom stiže kao `subscription_inactive` iz webhooka.
  */
  payment_required: { key: 'error.contentLocked', action: 'plans' },
  subscription_inactive: { key: 'error.subscriptionInactive', action: 'plans' },
  upload_failed: { key: 'error.uploadFailed', action: 'retry' },
  internal: { key: 'error.generic', action: 'retry' },
  network: { key: 'error.network', action: 'retry' },
};

/** Greška sinkronizacije nema svoj `ErrorCode` — nosi je poseban razred niže. */
export class SyncError extends Error {
  constructor(readonly pending: number) {
    super(`sync_failed:${pending}`);
    this.name = 'SyncError';
  }
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export function toErrorMessage(t: TFunction, error: unknown): ErrorMessage {
  if (error instanceof SyncError) {
    return {
      code: 'internal',
      title: t('error.syncFailed'),
      help: t('error.syncFailedHelp', { count: error.pending }),
      actionKey: 'sync',
    };
  }

  let code: ErrorCode | 'network';
  if (error instanceof AppError) {
    code = error.code;
  } else if (error instanceof TypeError || isOffline()) {
    // fetch baca TypeError kad mreže nema — jedini slučaj kad kod ne dolazi sa servera.
    code = 'network';
  } else {
    code = 'internal';
  }

  const entry = MAP[code];
  const title = t(entry.key);
  const helpKey = `${entry.key}Help`;
  const help = t(helpKey);
  return {
    code,
    title,
    help: help === helpKey ? t('error.genericHelp') : help,
    actionKey: entry.action,
  };
}

/**
 * Poruka za neuspjelu mutaciju. 409 je jedini kod čije značenje ovisi o radnji
 * (zauzet dan vs. pun tjedan), pa ga pozivatelj imenuje vlastitim ključem.
 */
export function toMutationMessage(t: TFunction, error: unknown, conflictKey?: string): string {
  if (conflictKey && error instanceof AppError && error.code === 'conflict') return t(conflictKey);
  return toErrorMessage(t, error).title;
}
